import {useEffect, useRef} from 'react';

import {getCoverAttributeDisplayNameForTile} from '@amblique/king-living/app/utils/customize-selections';

/**
 * @param {Object} variant
 * @returns {boolean} True when the cover has tracked stock (ATS > 0).
 */
function hasTrackedStock(variant) {
  const ats = typeof variant?.ats === 'number' ? variant.ats : null;

  return ats != null && ats > 0;
}

/**
 * Build the canonical cover-selection shape consumed by `CustomizeJourneyContext`.
 * Mirrors the object produced by `CoverStep.handleVariantSelect` and
 * `buildDefaultSelectionsFromData` so downstream consumers (AddToCartStrip,
 * ComplexAddToCartButton, ReviewSection gallery) do not need Freeform-specific code.
 *
 * @param {Object} variant - Enriched variant entry from `c_allVariantsData`.
 * @returns {{productId: string, variationValues: Object, name: string, salePrice: number|null, price: number|null, imageUrl: string|null}}
 */
export function buildCoverSelectionFromVariant(variant) {
  return {
    productId: variant.productId,
    variationValues: variant.variationValues || {},
    name: getCoverAttributeDisplayNameForTile(variant) || variant.name,
    salePrice: variant.salePrice ?? null,
    price: variant.price ?? null,
    imageUrl: variant.swatchImageUrl ?? null,
    selectedAt: Date.now(),
  };
}

/**
 * FSD §5.1 — on Simple PDP load, auto-select the first cover with tracked stock (ATS > 0).
 * CMTO masters (covers ATS N/A or 0) leave cover unselected (KINDW-1595).
 *
 * No-op when nothing has tracked stock or when the user already has a committed cover.
 *
 * The preselect fires at most once per master (guarded by a ref) so mutations to
 * `committedSelection.selections` — e.g. the user manually changing the cover — do
 * not trigger another auto-select.
 *
 * @param {Object} args
 * @param {boolean} args.enabled - Gate (e.g. `isSimpleMasterWithCovers`).
 * @param {Array<Object>} args.variants - Enriched cover variants (c_allVariantsData entries).
 * @param {Object|null} args.currentSelections - Latest `committedSelection.selections`.
 * @param {Function} args.commitInlineSelections - Journey-context commit fn.
 * @param {string|null|undefined} args.productId - Master product id (resets the guard when the PDP changes).
 */
export function useFreeformCoverPreselect({
  enabled,
  variants,
  currentSelections,
  commitInlineSelections,
  productId,
}) {
  const didFireRef = useRef(false);
  const lastProductIdRef = useRef(null);

  useEffect(() => {
    if (lastProductIdRef.current !== productId) {
      lastProductIdRef.current = productId;
      didFireRef.current = false;
    }
  }, [productId]);

  useEffect(() => {
    if (!enabled) return;
    if (didFireRef.current) return;
    if (!Array.isArray(variants) || variants.length === 0) return;
    if (typeof commitInlineSelections !== 'function') return;
    if (currentSelections?.cover?.productId) {
      didFireRef.current = true;

      return;
    }

    const firstInStock = variants.find(hasTrackedStock);

    if (!firstInStock) {
      // FSD §5.1 — leave no cover selected when nothing has tracked stock.
      didFireRef.current = true;

      return;
    }

    didFireRef.current = true;

    const baseSelections = currentSelections && typeof currentSelections === 'object'
      ? currentSelections
      : {};

    commitInlineSelections({
      ...baseSelections,
      cover: buildCoverSelectionFromVariant(firstInStock),
    });
  }, [enabled, variants, currentSelections, commitInlineSelections]);
}
